import * as THREE from 'three';
import type { MapDefinition, MapPath } from '../../../shared/game/world-types.js';
import { terrainGrid, terrainHeight } from '../../../shared/game/terrain.js';

export const ROAD_SURFACE_LIFT = .002;

/** Paint the road onto copies of the ground's own triangles, so it cannot bridge over the feet. */
export function terrainRoadGeometry(path: MapPath, curve: THREE.CatmullRomCurve3, map: MapDefinition, fade: number) {
  const { columns, rows } = terrainGrid(map), dx = map.width / columns, dz = map.depth / rows;
  const reach = path.width / 2 + fade, padding = reach + Math.max(dx, dz);
  type Segment = { ax: number; az: number; bx: number; bz: number; along: number; length: number };
  const cells = new Map<number, Segment[]>(), count = Math.max(12, Math.ceil(curve.getLength() / .65));
  let previous = curve.getPoint(0), travelled = 0;
  for (let i = 1; i <= count; i++) {
    const next = curve.getPoint(i / count), length = Math.hypot(next.x - previous.x, next.z - previous.z);
    const segment = { ax: previous.x, az: previous.z, bx: next.x, bz: next.z, along: travelled, length };
    const x0 = Math.max(0, Math.floor((Math.min(previous.x, next.x) - padding + map.width / 2) / dx));
    const x1 = Math.min(columns - 1, Math.floor((Math.max(previous.x, next.x) + padding + map.width / 2) / dx));
    const z0 = Math.max(0, Math.floor((Math.min(previous.z, next.z) - padding + map.depth / 2) / dz));
    const z1 = Math.min(rows - 1, Math.floor((Math.max(previous.z, next.z) + padding + map.depth / 2) / dz));
    for (let z = z0; z <= z1; z++) for (let x = x0; x <= x1; x++) {
      const key = z * columns + x, bucket = cells.get(key);
      if (bucket) bucket.push(segment); else cells.set(key, [segment]);
    }
    previous = next; travelled += length;
  }
  const positions: number[] = [], across: number[] = [], along: number[] = [], indices: number[] = [];
  const vertices = new Map<number, number>();
  const vertex = (x: number, z: number, segments: Segment[]) => {
    const key = z * (columns + 1) + x, existing = vertices.get(key);
    if (existing !== undefined) return existing;
    const px = x * dx - map.width / 2, pz = z * dz - map.depth / 2;
    let best = Infinity, side = 0, distance = 0;
    for (const s of segments) {
      const vx = s.bx - s.ax, vz = s.bz - s.az, square = vx * vx + vz * vz;
      const t = square > 1e-10 ? THREE.MathUtils.clamp(((px - s.ax) * vx + (pz - s.az) * vz) / square, 0, 1) : 0;
      const ox = px - s.ax - t * vx, oz = pz - s.az - t * vz, d = ox * ox + oz * oz;
      if (d < best) {
        best = d; side = Math.sqrt(d) * (vx * oz - vz * ox >= 0 ? 1 : -1);
        distance = s.along + s.length * t;
        // Extend the end coordinate past the caps so interpolated fragments cannot grow a zigzag tail.
        if (s.length > 1e-5 && (s.along === 0 && t === 0 || s.along + s.length >= travelled - 1e-5 && t === 1)) {
          distance += (ox * vx + oz * vz) / s.length;
        }
      }
    }
    const index = positions.length / 3; vertices.set(key, index);
    positions.push(px, terrainHeight(map, px, pz) + ROAD_SURFACE_LIFT, pz); across.push(side); along.push(distance);
    return index;
  };
  for (const [key, segments] of cells) {
    const x = key % columns, z = Math.floor(key / columns);
    // All four corners use the same nearby segment list; neighbour buckets contain the endpoint candidates too.
    const nearby = new Set(segments);
    for (let oz = -1; oz <= 1; oz++) for (let ox = -1; ox <= 1; ox++) {
      if (x + ox < 0 || x + ox >= columns || z + oz < 0 || z + oz >= rows) continue;
      for (const s of cells.get((z + oz) * columns + x + ox) ?? []) nearby.add(s);
    }
    const list = [...nearby], a = vertex(x, z, list), b = vertex(x, z + 1, list), c = vertex(x + 1, z + 1, list), d = vertex(x + 1, z, list);
    indices.push(a, b, d, b, c, d);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('across', new THREE.Float32BufferAttribute(across, 1));
  geometry.setAttribute('along', new THREE.Float32BufferAttribute(along, 1));
  geometry.setIndex(indices); geometry.computeVertexNormals();
  geometry.userData.roadLength = travelled;
  return geometry;
}
